// eslint-disable-next-line import/no-internal-modules
import { common } from '@insided/deprecated-preact-components/common'
// eslint-disable-next-line import/no-internal-modules
import globals from '@insided/deprecated-utils/globals'
import { Component, ComponentChildren } from 'preact'
import classnames from 'classnames'

interface State {
  displayOriginal: boolean
}

export interface LazyImageProps {
  children?: ComponentChildren
  type: 'img' | 'background'
  style?: Object
  src: string
  srcSet?: string
  title?: string
  alt?: string
  className?: string
  displayOriginalImage?: boolean
}

interface LazyComponentType extends Element {
  src: string
  srcSet: string
  style: {
    backgroundImage: string
  }
  dataset: {
    src: string
  }
  offsetWidth: number
  offsetHeight: number
}

class LazyLoaderComponent extends Component<LazyImageProps, State> {
  ref: null | HTMLElement = null
  displayOriginal = false

  componentDidMount() {
    if (
      !common.isModuleActive('lazyLoading') ||
      globals('application') === 'control' ||
      !document.body.classList.contains('twig_site-index') ||
      this.ref === null
    ) {
      this.setState({ displayOriginal: true })
      return
    }

    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const lazyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // @ts-expect-error
            const lazyComponent: LazyComponentType = entry.target
            const componentHeight: number = lazyComponent.offsetHeight
              ? lazyComponent.offsetHeight * 2
              : window.innerHeight

            const checkIfImageResizedAlready = /attachment\/(width|height)\//
            const oldUrl = lazyComponent.dataset.src
            let newSrc = oldUrl

            if (!this.props.displayOriginalImage) {
              newSrc = oldUrl.match(checkIfImageResizedAlready)
                ? oldUrl
                : oldUrl.replace('/attachment/', `/attachment/height/${componentHeight}/`)
            }

            if (this.props.type === 'img') {
              lazyComponent.src = newSrc
            } else {
              lazyComponent.style.backgroundImage = `url(${newSrc})`
            }

            lazyComponent.classList.remove('lazy')
            lazyObserver.unobserve(lazyComponent)
          }
        })
      })

      lazyObserver.observe(this.ref)
    } else {
      this.setState({ displayOriginal: true })
    }
  }

  render() {
    const { children, type, style = {}, src, srcSet, title = '', alt = '', className } = this.props
    const { displayOriginal } = this.state
    const isBrowser = typeof window !== 'undefined'
    if (displayOriginal) {
      if (type === 'img') {
        return (
          <img className={className} src={src} srcset={srcSet} style={style} title={title} alt={alt || title} />
        )
      } else if (type === 'background') {
        return (
          <div
            role='img'
            aria-label={alt || title}
            className={className}
            style={{ ...style, 'background-image': `url(${src})` }}
          >
            {children}
          </div>
        )
      }
    }
    if (type === 'img') {
      return (
        <img
          ref={(ref) => {
            this.ref = ref
          }}
          src={isBrowser ? undefined : src}
          srcset={srcSet}
          className={classnames('lazy', className)}
          data-src={src}
          style={style}
          title={title}
          alt={alt || title}
        />
      )
    }
    return (
      <div
        role='img'
        aria-label={alt || title}
        ref={(ref) => {
          this.ref = ref
        }}
        className={classnames('lazy', className)}
        data-src={src}
        style={isBrowser ? undefined : `background-image: ${src}`}
      >
        {' '}
        {children}{' '}
      </div>
    )
  }
}

const LazyImage = (props: LazyImageProps) => <LazyLoaderComponent {...props} />

export default LazyImage
